"use client";

// components/publications/creation-flow/global-creation-modal.tsx
//
// Spec §E 1.6a: single context-owned mount of CreationModal, replacing the
// two per-nav mounts (sidebar-nav-client.tsx, bottom-nav-bar-client.tsx)
// that each carried their own local `isPostModalOpen` useState. Visibility
// is the shared overlay panel slot (`{ kind: "creation" }`); both nav
// buttons call `openPanel({ kind: "creation" })` instead of a local setter.
//
// Mount-cost profile: conditional on the panel kind, matching the
// CHEAPER of the two pre-migration mounts (sidebar's `isPostModalOpen &&
// user && <CreationModal .../>`) rather than bottom-nav's always-mounted
// `<CreationModal open={isPostModalOpen} .../>`. Collapsing onto the
// cheaper profile is a strict improvement for the bottom-nav call site (it
// no longer pays CreationFlowContainer's own hook/effect setup — useModalContainer,
// useMediaQuery, listing-draft state — while the modal is closed) and a
// no-op for the sidebar call site.
//
// Draft resume (2026-08-05): `{ kind: "creation", draftId }` routes through
// ResumeDraftGate — the draft row is fetched and parsed BEFORE the container
// mounts, so the form state seeds entirely in useState initializers (no
// hydrate-after-mount effects). `key={draftId}` guarantees a clean remount
// when a different draft is opened.
//
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §E row 1.6a

import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";

import { CreationModal } from "@/components/publications/creation-flow/creation-modal";
import { useOverlay } from "@/components/context/overlay-context";
import { useListingDraft } from "@/hooks/use-listing-drafts";
import { useToast } from "@/hooks/use-toast";
import { SHEET_Z_DEFAULT } from "@/lib/sheet-styles";
import type { TUser } from "@/types/User";

export function GlobalCreationModal({ user }: { user: NonNullable<TUser> }) {
  const { overlay, closePanel } = useOverlay();
  const panel = overlay.panel;

  if (panel.kind !== "creation") return null;

  if (panel.draftId) {
    return (
      <ResumeDraftGate
        key={panel.draftId}
        draftId={panel.draftId}
        user={user}
        closePanelAction={closePanel}
      />
    );
  }

  return (
    <CreationModal
      open
      onOpenChangeAction={(open) => {
        if (!open) closePanel();
      }}
      user={user}
    />
  );
}

function ResumeDraftGate({
  draftId,
  user,
  closePanelAction,
}: {
  draftId: string;
  user: NonNullable<TUser>;
  closePanelAction: () => void;
}) {
  const t = useTranslations("listingCreation.resumeDraft");
  const { toast } = useToast();
  const resume = useListingDraft(draftId);

  // Covers a row deleted on another device, a fetch failure, and an
  // unreadable payload version alike: notify and bail out of the panel.
  useEffect(() => {
    if (resume.kind !== "error") return;
    toast({ title: t("loadError"), variant: "destructive" });
    closePanelAction();
  }, [resume.kind, toast, t, closePanelAction]);

  if (resume.kind === "loading") {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        style={{ zIndex: SHEET_Z_DEFAULT }}
        aria-busy
      >
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }
  if (resume.kind === "error") return null;

  return (
    <CreationModal
      open
      onOpenChangeAction={(open) => {
        if (!open) closePanelAction();
      }}
      user={user}
      initialData={resume.data}
      initialStep={resume.step}
      draftId={draftId}
    />
  );
}
